'use client';

import { Image } from 'lucide-react';
import { useEffect, useState } from 'react';

import { BubbleAvatar } from '../common/bubble-avatar.ui';

import { ChatData } from '@/slw/entities/chat';
import { cn } from '@/slw/shared/lib/utils';
import { Button, buttonVariants } from '@/slw/shared/ui/button';
import { convertDate } from '@/slw/shared/lib/date';
import { useAuthStore } from '@/slw/shared/stores/auth.store';
import { onlineStatusService } from '@/slw/shared/lib/firebase/online-status';

interface ChatItemProps {
  chat: ChatData;
  isSelected: boolean;
  onClick: () => void;
}

export function ChatItem({ chat, isSelected, onClick }: ChatItemProps) {
  const { user } = useAuthStore();

  const isSelfMessage = chat?.senderId === user?.id;
  const [isOnline, setIsOnline] = useState(chat?.user?.isOnline || false);

  // Theo dõi trạng thái online của người dùng
  useEffect(() => {
    if (!chat?.user?.id) return;

    const unsubscribe = onlineStatusService.getOnlineStatus(chat.user.id, (online) => {
      setIsOnline(online);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [chat?.user?.id]);

  if (!chat?.user) {
    return null;
  }

  return (
    <Button
      onClick={onClick}
      className={cn(
        buttonVariants({
          color: isSelected ? 'secondary' : 'light',
          variant: 'ghost',
          size: 'lg',
        }),
        isSelected && 'bg-accent text-primary shrink',
        'justify-start gap-4 w-full cursor-pointer sm:px-1 px-2',
      )}
    >
      <div className='relative'>
        <BubbleAvatar src={chat.user.avatar} name={chat.user.username} />
        <div
          className={cn(
            'absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white',
            isOnline ? 'bg-green-500' : 'bg-gray-400',
          )}
          title={isOnline ? 'Online' : 'Offline'}
        />
      </div>
      <div className='flex flex-col flex-grow'>
        <div className='flex justify-between items-center'>
          <span className='text-left'>{chat.user.username}</span>
          {!chat.isSeen && !isSelfMessage && (
            <div className='h-2 w-2 rounded-full bg-blue-500' title='Unread message'></div>
          )}
        </div>
        {chat.lastMessage && (
          <div className='flex justify-between items-center'>
            {Array.isArray(chat.lastMessage) ? (
              <span className='text-zinc-400 text-left text-xs max-w-28 flex items-center '>
                {isSelfMessage ? 'You: ' : ''}
                <span className='inline-flex items-center gap-1'>
                  <Image size={16} />
                  Image
                </span>
              </span>
            ) : (
              <span
                className='text-zinc-400 text-left text-xs overflow-hidden
              truncate text-ellipsis max-w-28 '
              >
                {isSelfMessage ? 'You: ' : ''} {chat.lastMessage}
              </span>
            )}
            {chat.updatedAt && <span className='text-zinc-400 text-xs'>{convertDate(chat.updatedAt)}</span>}
          </div>
        )}
      </div>
    </Button>
  );
}
